import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { Typography, colors, Loading, Button } from '@leapeasy/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalUXState } from 'components/GlobalUXState';
import { DetailPageLoadingCenter } from 'components/DetailPageLoadingCenter';
import { useAuthSession } from 'hooks/useAuthSession';

import {
  getUserCountAction,
  getAppReportAction,
  getBuildingCountAction,
  getLandlordCountAction,
  getFlaggedCancelReportAction,
} from 'store/actions/reportActions';
import { isFlaggedCancellationsUiVisible } from 'utils/flaggedCancellationUi';
import { BuildingCount } from './Reports/BuildingCount';
import { UserCount } from './Reports/UserCount';
import { AdminReport } from './Reports/Admin';

export const Dashboard = () => {
  const dispatch = useDispatch();
  const { user: currentUser, isSessionComplete } = useAuthSession();

  const adminReportData = useSelector((state) => state.getIn(['report', 'adminAppReport']));
  const buildingCount = useSelector((state) => state.getIn(['report', 'buildingCount']));
  const userCount = useSelector((state) => state.getIn(['report', 'userCount']));
  const landlordCountState = useSelector((state) => state.getIn(['report', 'landlordCount']));
  const userCountError = useSelector((state) => state.getIn(['report', 'userCountError']));
  const buildingCountError = useSelector((state) => state.getIn(['report', 'buildingCountError']));
  const appReportError = useSelector((state) => state.getIn(['report', 'appReportError']));

  const [showSlowLoadingState, setShowSlowLoadingState] = useState(false);

  useEffect(() => {
    dispatch(getUserCountAction());
    dispatch(getLandlordCountAction());
    if (!buildingCount) {
      dispatch(getBuildingCountAction());
    }
    if (!adminReportData) {
      dispatch(getAppReportAction());
    }
    if (isFlaggedCancellationsUiVisible()) {
      dispatch(getFlaggedCancelReportAction());
    }
    // Mount-only: preserve cached building/app report slices when returning to dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardsReady = Boolean(userCount && buildingCount);
  const cardsFailed = Boolean(userCountError || buildingCountError);

  useEffect(() => {
    if (cardsReady || cardsFailed) {
      setShowSlowLoadingState(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowSlowLoadingState(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [cardsFailed, cardsReady]);

  const reloadDashboard = useCallback(() => {
    setShowSlowLoadingState(false);
    dispatch(getUserCountAction());
    dispatch(getBuildingCountAction());
    dispatch(getLandlordCountAction());
    dispatch(getAppReportAction());
    if (isFlaggedCancellationsUiVisible()) {
      dispatch(getFlaggedCancelReportAction());
    }
  }, [dispatch]);

  const userCountPayload = useMemo(() => userCount?.toJS?.() ?? null, [userCount]);
  const buildingCountPayload = useMemo(() => buildingCount?.toJS?.() ?? null, [buildingCount]);
  const adminReportPayload = useMemo(() => adminReportData?.toJS?.() ?? null, [adminReportData]);
  const landlordCountPayload = useMemo(
    () => landlordCountState?.toJS?.() ?? null,
    [landlordCountState],
  );

  if (!isSessionComplete || !currentUser) {
    return (
      <DashboardLayoutContainer>
        <DetailPageLoadingCenter label="Loading dashboard..." />
      </DashboardLayoutContainer>
    );
  }

  const renderCards = () => {
    if (cardsFailed && !cardsReady) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
          <GlobalUXState
            variant="apiError"
            compact
            description={
              [userCountError, buildingCountError].filter(Boolean).join(' ') ||
              'Unable to load dashboard metrics.'
            }
          />
          <Button variant="primary" size="medium" onClick={reloadDashboard}>
            Retry
          </Button>
        </Box>
      );
    }

    if (!cardsReady) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
          <Loading size="medium" isPathVisible />
          {showSlowLoadingState ? (
            <GlobalUXState
              variant="slowLoading"
              compact
              description="Dashboard cards are still loading."
            />
          ) : null}
        </Box>
      );
    }

    return (
      <>
        <TotalInfoContainer display="flex" flexDirection="column" gap="20px">
          <UserCount
            data={userCountPayload?.data ?? []}
            viewerRole={currentUser.role}
            landlordCount={landlordCountPayload?.data}
            activeLandlordCount={userCountPayload?.activeLandlordCount}
            vpDelegationTotal={userCountPayload?.vpDelegationTotal}
            rmDelegationTotal={userCountPayload?.rmDelegationTotal}
            pmDelegationTotal={userCountPayload?.pmDelegationTotal}
          />
          <BuildingCount data={buildingCountPayload?.data ?? {}} />
        </TotalInfoContainer>

        {appReportError && !adminReportPayload?.data ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap="12px" mt={1}>
            <GlobalUXState variant="apiError" compact description={appReportError} />
            <Button variant="secondary" size="medium" onClick={reloadDashboard}>
              Retry charts
            </Button>
          </Box>
        ) : null}

        {adminReportPayload?.data ? <AdminReport data={adminReportPayload.data} /> : null}
      </>
    );
  };

  return (
    <DashboardLayoutContainer>
      <Box display="flex" flexDirection="column" gap="24px" style={{ color: colors.purple[900] }}>
        <Typography variant="h3" style={{ fontWeight: '500' }}>
          Dashboard
        </Typography>
        {renderCards()}
      </Box>
    </DashboardLayoutContainer>
  );
};

const TotalInfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media screen and (max-width: ${({ theme }) => theme['lg']}) {
    & > div {
      flex-direction: column;
    }

    @media screen and (max-width: ${({ theme }) => theme['md']}) {
      & > div:nth-of-type(2) {
        & > div {
          & > div:nth-of-type(2) {
            flex-direction: column;
          }
        }
      }
    }
  }
`;
